// Global banner shown when the current user's Google authorization has
// died. Visible on every page until the operator clicks Reconnect.
//
// The banner posts to ``/google/auth-url`` then redirects the browser
// to the returned URL; Google redirects back to ``/google/callback``
// which exchanges the code and updates the cached auth-status query.
import { useGoogleAuthStatus } from "../api/hooks/useDriveSync";
import { useConnectGoogle } from "../utils/useConnectGoogle";
import { useDeferredReady } from "../utils/useDeferredReady";
import { NoticeBanner } from "./NoticeBanner";

export const ReconnectBanner = () => {
  // Deferred off the initial page-load burst: the banner is a non-blocking
  // nudge, so it can resolve a beat after the page's own data instead of
  // competing for a Lambda container on the critical path.
  const deferred = useDeferredReady();
  const { data, isLoading } = useGoogleAuthStatus({ enabled: deferred });
  const { connect: handleReconnect, connecting } = useConnectGoogle(
    data?.authorization?.user_email,
  );

  if (isLoading || !data) {
    return null;
  }
  const auth = data.authorization;
  // Don't show the banner unless the user actually has a failed
  // authorization. New users (no row) see it only when they try to
  // configure Drive sync.
  if (!auth || auth.status !== "failed") {
    return null;
  }
  const failedCount = data.failed_project_ids.length;
  return (
    <NoticeBanner
      action={connecting ? "Redirecting…" : "Reconnect Drive"}
      onAction={handleReconnect}
      busy={connecting}
    >
      Google Drive sync is paused
      {failedCount > 0
        ? ` on ${failedCount} project${failedCount === 1 ? "" : "s"}`
        : ""}
      {" "}because your authorization expired
      {auth.last_failure_reason ? ` (${auth.last_failure_reason})` : ""}.
    </NoticeBanner>
  );
};
